import { CanvasTexture, LinearMipmapLinearFilter, SRGBColorSpace } from 'three';

type TCanvasTextureSlotParams = {
    anisotropy?: number;
    // Explicit flipY — defaults to Three's `true`. Gobelet-style paths
    // (fabric → cylinder wrap) set false; procedural mesh paths that
    // built their own UVs assuming the Three default keep true.
    flipY?: boolean;
    height: number;
    mipmaps?: boolean;
    width: number;
};

type TPaintOptions = {
    // Fill the backing canvas with white before drawImage — needed
    // when the target material would render alpha=0 as black (e.g.
    // MeshStandardMaterial on paper).
    backgroundFill?: 'white';
    sourceRect?: { height: number; width: number; x: number; y: number };
};

// Owns one backing HTMLCanvasElement + one CanvasTexture bound to it.
// Reused across frames: paint() redraws the backing canvas and flips
// texture.needsUpdate = true — no per-frame GPU allocation.
class CanvasTextureSlot {
    private readonly _backing: HTMLCanvasElement;
    private readonly _texture: CanvasTexture;

    constructor(params: TCanvasTextureSlotParams) {
        this._backing = document.createElement('canvas');
        this._backing.width = params.width;
        this._backing.height = params.height;

        this._texture = new CanvasTexture(this._backing);
        this._texture.colorSpace = SRGBColorSpace;
        this._texture.flipY = params.flipY ?? true;
        if (params.anisotropy !== undefined) {
            this._texture.anisotropy = params.anisotropy;
        }
        if (params.mipmaps) {
            this._texture.generateMipmaps = true;
            this._texture.minFilter = LinearMipmapLinearFilter;
        }
    }

    get height(): number {
        return this._backing.height;
    }

    get texture(): CanvasTexture {
        return this._texture;
    }

    get width(): number {
        return this._backing.width;
    }

    dispose(): void {
        this._texture.dispose();
    }

    paint(source: HTMLCanvasElement, options: TPaintOptions = {}): void {
        const ctx = this._backing.getContext('2d');
        if (!ctx) {
            throw new Error('CanvasTextureSlot: 2D context not available on backing canvas');
        }
        // Always clear before drawImage so transparent zones in the
        // snapshot translate to transparent pixels on the backing
        // (never layering on top of the previous frame). If a white
        // fallback is requested (paper strategies where alpha=0 would
        // render black on MeshStandardMaterial), fillRect afterwards.
        ctx.clearRect(0, 0, this._backing.width, this._backing.height);
        if (options.backgroundFill === 'white') {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, this._backing.width, this._backing.height);
        }
        const rect = options.sourceRect ?? { height: source.height, width: source.width, x: 0, y: 0 };
        ctx.drawImage(source, rect.x, rect.y, rect.width, rect.height, 0, 0, this._backing.width, this._backing.height);
        this._texture.needsUpdate = true;
    }
}

export { CanvasTextureSlot };
